<template>

  <div class="flex flex-row text-l justify-center gap-4">
    <button v-if="selectedIndex === 0" class="cursor-pointer bg-gradient-to-r from-green-400 to-teal-400 text-gray-900">
      Daily
    </button>

    <button v-else class="cursor-pointer" @click="leaderboardSelected()">Daily</button>

    <button v-if="selectedIndex === 1" class="cursor-pointer bg-gradient-to-r from-green-400 to-teal-400 text-gray-900">
      All-time
    </button>
    <button v-else class="cursor-pointer" @click="rankingSelected()">All-time</button>

    <button v-if="selectedIndex === 2" class="cursor-pointer bg-gradient-to-r from-green-400 to-teal-400 text-gray-900">
      Achievements
    </button>
    <button v-else class="cursor-pointer" @click="achievementsSelected()">Achievements</button>

    <button v-if="selectedIndex === 3" class="cursor-pointer bg-gradient-to-r from-green-400 to-teal-400 text-gray-900">
      Stats
    </button>
    <button v-else class="cursor-pointer" @click="statsSelected()">Stats</button>
  </div>
  <Leaderboard v-if="selectedIndex === 0" />
  <Ranking v-else-if="selectedIndex === 1"/>
  <AchievementLeaderboard v-else-if="selectedIndex === 2"/>
  <div v-else-if="selectedIndex === 3">
    <loading-spinner v-if="loading"></loading-spinner>
    <div v-if="stats && !loading" class="flex flex-col justify-between mt-4 gap-2.5">

      <div class="flex flex-col items-center">
        <p class="text-2xl text-gradient font-extrabold leading-tight">Level {{ currentLevel }}</p>
        <div class="relative w-full h-2.5 mt-1 bg-gray-600 rounded-full overflow-hidden">
          <div
            class="bg-gradient-to-r from-green-400 to-teal-400 h-2.5 absolute top-0 left-0"
            :style="{ width: `${levelProgress}%` }"></div>
        </div>
        <div class="text-xs text-gray-400 mt-0.5">{{ xp }} / {{ nextLevelXp }} XP</div>
      </div>

      <div class="grid grid-cols-3 gap-2">
        <div class="flex flex-col items-center bg-gray-800 rounded-lg py-1.5 px-1">
          <div class="flex items-center gap-1.5">
            <font-awesome-icon :icon="['fas', 'gamepad']" class="text-green-400 text-xs"/>
            <p class="text-lg text-gradient font-extrabold">{{ format(stats.allTimeGamesPlayed) }}</p>
          </div>
          <div class="text-xs text-gray-400">Played</div>
        </div>

        <div class="flex flex-col items-center bg-gray-800 rounded-lg py-1.5 px-1">
          <div class="flex items-center gap-1.5">
            <font-awesome-icon :icon="['fas', 'star']" class="text-yellow-400 text-xs"/>
            <p class="text-lg text-gradient font-extrabold">{{ format(stats.allTimeScore) }}</p>
          </div>
          <div class="text-xs text-gray-400">Total Score</div>
        </div>

        <div class="flex flex-col items-center bg-gray-800 rounded-lg py-1.5 px-1">
          <div class="flex items-center gap-1.5">
            <font-awesome-icon :icon="['fas', 'chart-line']" class="text-blue-300 text-xs"/>
            <p class="text-lg text-gradient font-extrabold">{{ averageScore }}</p>
          </div>
          <div class="text-xs text-gray-400">Average Score</div>
        </div>

        <div class="flex flex-col items-center bg-gray-800 rounded-lg py-1.5 px-1">
          <div class="flex items-center gap-1.5">
            <font-awesome-icon :icon="['fas', 'trophy']" class="text-amber-400 text-xs"/>
            <p class="text-lg text-gradient font-extrabold">{{ bestScore }}</p>
          </div>
          <div class="text-xs text-gray-400">Best Score</div>
        </div>

        <div class="flex flex-col items-center bg-gray-800 rounded-lg py-1.5 px-1">
          <div class="flex items-center gap-1.5">
            <font-awesome-icon :icon="['fas', 'fire']" class="text-orange-400 text-xs"/>
            <p class="text-lg text-gradient font-extrabold">{{ stats.streak }}</p>
          </div>
          <div class="text-xs text-gray-400">Current Streak</div>
        </div>

        <div class="flex flex-col items-center bg-gray-800 rounded-lg py-1.5 px-1">
          <div class="flex items-center gap-1.5">
            <font-awesome-icon :icon="['fas', 'crown']" class="text-amber-300 text-xs"/>
            <p class="text-lg text-gradient font-extrabold">{{ stats.allTimeBestStreak }}</p>
          </div>
          <div class="text-xs text-gray-400">Best Streak</div>
        </div>
      </div>

      <div class="h-40">
        <canvas id="myChart"></canvas>
      </div>

      <ScoreCalendar :scores="stats.scores"/>
    </div>
  </div>
</template>
<script setup>

import Leaderboard from "./Leaderboard.vue";
import {computed, onMounted, ref, watch} from "vue";
import {useStats} from "../composables/useStats.js";
import LoadingSpinner from "./LoadingSpinner.vue";
import {Chart, registerables} from "chart.js";
import Ranking from "./Rankings.vue";
import AchievementLeaderboard from "./AchievementLeaderboard.vue";
import ScoreCalendar from "./ScoreCalendar.vue";
import {useUserState} from "../composables/useUserState.js";
import {useLevels} from "../composables/useLevels.js";
import {useFormat} from "../composables/useFormat.js";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

Chart.register(...registerables);
const {getStats, stats, loading} = useStats();
const {syndleUser} = useUserState();
const {calculateCurrentLevel, calculateNextLevelXp} = useLevels();
const {formatCompact: format} = useFormat();

const levelConstant = 50;

const emit = defineEmits(['tab-change']);
const tabTitles = ['Daily', 'All-time', 'Achievements', 'Stats'];

let selectedIndex = ref(0);

watch(selectedIndex, (i) => emit('tab-change', tabTitles[i]), {immediate: true});

onMounted(async () => {
  await leaderboardSelected()
})

let chartInstance = null;

// Vertical fade under the score line: solid-ish green at the top of the
// plot area down to fully transparent at the baseline.
function scoreGradient(context) {
  const {ctx, chartArea} = context.chart;
  if (!chartArea) {
    return 'rgba(66,222,136,0.2)';
  }
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, 'rgba(66,222,136,0.45)');
  gradient.addColorStop(1, 'rgba(66,222,136,0)');
  return gradient;
}

watch(stats, () => {
  const ctx = document.getElementById('myChart');
  const scores = stats.value.scores;
  let averages = [];
  let total = 0;
  for (let i = 0; i < scores.length; i++) {
    const index = i + 1;
    const current = scores[i].score;
    total += current;
    averages.push(total / index);
  }
  const labels = scores.map(e => e.date);
  const data = {
    labels: labels,
    datasets: [{
      data: scores.map(e => e.score),
      fill: true,
      backgroundColor: scoreGradient,
      borderColor: 'rgb(66,222,136)',
      borderWidth: 2.5,
      tension: 0.4,
      label: `Day's Score`
    }, {
      data: averages,
      fill: false,
      borderColor: 'rgba(120,195,251,0.9)',
      borderWidth: 1.5,
      borderDash: [5, 4],
      tension: 0.4,
      label: `Average Score`
    }]
  };

  if (chartInstance) {
    chartInstance.destroy();
  }

  chartInstance = new Chart(ctx, {
    type: 'line',
    data: data,
    options: {
      responsive: true,
      maintainAspectRatio: false,
      borderCapStyle: 'round',
      interaction: {mode: 'index', intersect: false},
      elements: {
        point: {radius: 0, hoverRadius: 4, hitRadius: 12}
      },
      plugins: {
        legend: {
          labels: {
            usePointStyle: true,
            pointStyle: 'line',
            boxWidth: 24,
            color: 'rgba(255,255,255,0.7)',
            font: {size: 11}
          }
        },
        tooltip: {
          backgroundColor: 'rgba(26,26,26,0.95)',
          borderColor: 'rgba(255,255,255,0.1)',
          borderWidth: 1,
          padding: 10,
          cornerRadius: 8,
          displayColors: false
        }
      },
      scales: {
        x: {
          ticks: {
            display: true,
            autoSkip: true,
            maxTicksLimit: 4,
            maxRotation: 0,
            color: 'rgba(255,255,255,0.45)',
            font: {size: 10}
          },
          grid: {display: false},
          border: {display: false}
        },
        y: {
          beginAtZero: true,
          ticks: {color: 'rgba(255,255,255,0.45)', font: {size: 10}, maxTicksLimit: 5},
          grid: {color: 'rgba(255,255,255,0.06)'},
          border: {display: false}
        }
      }
    }
  });
})

function leaderboardSelected() {
  selectedIndex.value = 0;
}

function rankingSelected() {
  selectedIndex.value = 1;
}

function achievementsSelected() {
  selectedIndex.value = 2;
}

async function statsSelected() {
  selectedIndex.value = 3;
  await getStats()
}

const averageScore = computed(() => {
  if (stats.value.allTimeGamesPlayed === 0) {
    return 0;
  }
  return (stats.value.allTimeScore / stats.value.allTimeGamesPlayed).toFixed(1).replace(/\.?0+$/, "");
})

const xp = computed(() => syndleUser.value.xp ?? 0);

const currentLevel = computed(() => calculateCurrentLevel(xp.value));

const nextLevelXp = computed(() => calculateNextLevelXp(xp.value, currentLevel.value));

const levelProgress = computed(() => {
  const currentLevelXp = nextLevelXp.value - levelConstant * currentLevel.value ** 1.2;
  const xpProgress = xp.value - currentLevelXp;
  const xpForNext = nextLevelXp.value - currentLevelXp;
  return Math.min((xpProgress / xpForNext) * 100, 100);
})

const bestScore = computed(() => {
  const scores = stats.value.scores;
  if (!scores || !scores.length) {
    return 0;
  }
  return Math.max(...scores.map(e => e.score));
})

</script>